import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import stripeService from '../../services/stripeService';
import { useTenant } from '../../contexts/TenantContext';

// Import Material UI components
import {
  Box, Typography, Paper, Button, Divider, Alert, CircularProgress, Grid,
  Card, CardContent, CardActions, List, ListItem, ListItemIcon, ListItemText,
  Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Container
} from '@mui/material';

// Import Material UI icons
import {
  CheckCircle as CheckCircleIcon,
  StarOutline as StarIcon,
  Star as StarFilledIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';

// Format currency
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const AdminPlans = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant(); // Get tenant from context
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    planId: null,
    planName: ''
  });
  const [processingCheckout, setProcessingCheckout] = useState(false);

  useEffect(() => {
    const loadPlansAndSubscription = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch real plans data from the API
        const plansResponse = await stripeService.getPlatformPlans();
        
        if (plansResponse && plansResponse.success && plansResponse.data) {
          setPlans(plansResponse.data);
        } else {
          setError("Could not load subscription plans. Please try again later.");
        }
        
        // Fetch current subscription if exists - using the appropriate method from stripeService
        try {
          // Note: We're using getTenantSubscription with a null parameter
          // This will retrieve the subscription for the current tenant
          const subscriptionResponse = await stripeService.getTenantSubscription();
          if (subscriptionResponse && subscriptionResponse.success && subscriptionResponse.data) {
            setCurrentSubscription(subscriptionResponse.data);
          }
        } catch (subError) {
          console.error("Failed to load current subscription:", subError);
          // Don't set an error - having no subscription is a valid state
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Failed to load plans:", err);
        setError("Failed to load subscription plans. Please refresh and try again.");
        setLoading(false);
      }
    };
    
    loadPlansAndSubscription();
  }, []);
  
  const handleSubscribe = async (planId, planName) => {
    setConfirmDialog({
      open: true,
      planId,
      planName
    });
  };
  
  const confirmSubscription = async () => {
    try {
      setProcessingCheckout(true);
      
      // Create a checkout session using stripeService
      const successUrl = `${window.location.origin}/admin/subscription/success`;
      const cancelUrl = `${window.location.origin}/admin/subscription/cancel`;
      
      // Get the tenant ID to include in the checkout request
      const tenantId = tenant?._id;
      
      if (!tenantId) {
        setError("Unable to identify your organization. Please refresh the page and try again.");
        setProcessingCheckout(false);
        return;
      }
      
      console.log(`Creating checkout session for plan: ${confirmDialog.planId}, tenant: ${tenantId}`);
      
      const response = await stripeService.createCheckoutSession(
        confirmDialog.planId, 
        successUrl, 
        cancelUrl,
        tenantId // Pass the tenant ID to the service
      );
      
      console.log('Checkout session response:', response);
      
      // Ensure we handle the response structure correctly
      if (response && response.data && response.data.url) {
        // Redirect to Stripe checkout
        console.log(`Redirecting to: ${response.data.url}`);
        window.location.href = response.data.url;
      } else if (response && response.data && response.data.checkoutUrl) {
        // Alternative response structure
        console.log(`Redirecting to: ${response.data.checkoutUrl}`);
        window.location.href = response.data.checkoutUrl;
      } else if (response && response.checkoutUrl) {
        // Another alternative response structure
        console.log(`Redirecting to: ${response.checkoutUrl}`);
        window.location.href = response.checkoutUrl;
      } else {
        console.error("Invalid response format from checkout API:", response);
        setError("Could not create checkout session. The server response was invalid.");
        setProcessingCheckout(false);
      }
      
    } catch (err) {
      console.error("Failed to start subscription process:", err);
      let errorMsg = "Failed to initiate subscription process.";
      
      // Get more specific error message if available
      if (err.response && err.response.data) {
        console.error("Server error details:", err.response.data);
        if (err.response.data.message) {
          errorMsg += " " + err.response.data.message;
        }
        if (err.response.data.error) {
          errorMsg += " Error: " + err.response.data.error;
        }
      } else if (err.message) {
        errorMsg += " " + err.message;
      }
      
      setError(errorMsg);
      setProcessingCheckout(false);
    } finally {
      // Only close the dialog if we failed to redirect
      if (document.body.contains(document.activeElement)) {
        setConfirmDialog({
          open: false,
          planId: null,
          planName: ''
        });
      }
    }
  };
  
  const handleManageSubscription = () => {
    navigate('/admin/subscription/details');
  };
  
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4 }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: '#f5f7fa',
        }}
      >
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <CreditCardIcon sx={{ mr: 1 }} /> Subscription Plans
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Select the best plan for your organization
          </Typography>
        </Paper>

        {/* Current Subscription Alert */}
        {currentSubscription && (
          <Alert 
            severity="info" 
            sx={{ mb: 4 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleManageSubscription}
              >
                Manage
              </Button>
            }
          >
            You're currently on the <strong>{currentSubscription.planName}</strong> plan. 
            {currentSubscription.currentPeriodEnd && (
              <span> Your subscription will renew on {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}.</span>
            )}
            {currentSubscription.cancelAtPeriodEnd && (
              <strong> Your subscription will be canceled at the end of the current period.</strong>
            )}
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert 
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* No plans message */}
        {plans.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No subscription plans are currently available. Please contact support for assistance.
          </Alert>
        )}

        {/* Plans Grid */}
        <Grid container spacing={3}>
          {plans.map((plan) => {
            const isCurrentPlan = currentSubscription && 
              (currentSubscription.planId === plan.id || currentSubscription.planName?.toLowerCase() === plan.name.toLowerCase());
            
            return (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card 
                  elevation={plan.recommended ? 3 : 1} 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    },
                    border: isCurrentPlan 
                      ? '2px solid #4caf50'
                      : plan.recommended 
                        ? '2px solid #3f51b5' 
                        : 'none',
                    opacity: plan.active ? 1 : 0.7
                  }}
                >
                  {plan.recommended && !isCurrentPlan && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        zIndex: 1
                      }}
                    >
                      <Chip
                        label="Recommended"
                        color="primary"
                        size="small"
                        icon={<StarFilledIcon />}
                      />
                    </Box>
                  )}

                  {isCurrentPlan && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        zIndex: 1
                      }}
                    >
                      <Chip
                        label="Current Plan"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                    </Box>
                  )}
                  
                  <CardContent sx={{ pt: (isCurrentPlan || plan.recommended) ? 4 : 2 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {plan.name}
                      {!plan.active && (
                        <Chip size="small" label="Inactive" color="default" sx={{ ml: 1, verticalAlign: 'middle' }} />
                      )}
                    </Typography>
                    
                    <Typography 
                      variant="h4" 
                      component="div" 
                      color="primary"
                      sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}
                    >
                      {formatCurrency(plan.price)}
                      <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                        /{plan.interval}
                      </Typography>
                    </Typography>

                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 3, minHeight: 60 }}
                    >
                      {plan.description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Features:
                    </Typography>
                    
                    <List dense disablePadding>
                      {plan.features && plan.features.map((feature, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
                    {isCurrentPlan ? (
                      <Button 
                        variant="outlined" 
                        color="success" 
                        fullWidth
                        onClick={handleManageSubscription}
                      >
                        Manage Subscription
                      </Button>
                    ) : (
                      <Button 
                        variant={plan.recommended ? "contained" : "outlined"}
                        color="primary"
                        fullWidth
                        onClick={() => handleSubscribe(plan.id, plan.name)}
                        disabled={!plan.active || processingCheckout}
                        sx={plan.recommended ? {
                          background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                          boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                        } : {}}
                      >
                        {currentSubscription ? 'Change Plan' : 'Subscribe'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Subscription Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => !processingCheckout && setConfirmDialog(prev => ({ ...prev, open: false }))}
          aria-labelledby="subscription-dialog-title"
        >
          <DialogTitle id="subscription-dialog-title">
            {currentSubscription ? 'Change Subscription Plan?' : 'Confirm Subscription'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {currentSubscription 
                ? `You are about to change your subscription from ${currentSubscription.planName} to ${confirmDialog.planName}. You will be charged the new rate immediately.`
                : `You are about to subscribe to the ${confirmDialog.planName} plan. You will be redirected to our payment processor to complete the subscription.`
              }
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
              color="inherit"
              disabled={processingCheckout}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmSubscription}
              variant="contained" 
              color="primary"
              autoFocus
              disabled={processingCheckout}
              startIcon={processingCheckout ? <CircularProgress size={20} /> : null}
            >
              {processingCheckout ? 'Processing...' : 'Proceed'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminPlans;
